import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { hasHigherOrEqualRole } from '@/lib/permissions';
import { supabase } from '@/integrations/supabase/client';
import {
  getSystemHealth,
  updateIntegrationStatus,
  logIntegrationTest,
  logSystemHealthCheck,
  SystemHealthStatus,
  IntegrationStatus,
} from '@/lib/integrations-service';
import {
  Activity,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Loader2,
  Calendar,
  CreditCard,
  Mail,
  Clock,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';

const statusConfig = {
  healthy: { label: 'Healthy', icon: CheckCircle, className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
  degraded: { label: 'Degraded', icon: AlertTriangle, className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' },
  down: { label: 'Down', icon: XCircle, className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
};

const integrationStatusConfig: Record<IntegrationStatus, { label: string; className: string }> = {
  success: { label: 'OK', className: 'text-green-600' },
  warning: { label: 'Warning', className: 'text-yellow-600' },
  error: { label: 'Error', className: 'text-destructive' },
  not_configured: { label: 'Not Configured', className: 'text-muted-foreground' },
  not_checked: { label: 'Not Checked', className: 'text-muted-foreground' },
};

interface IntegrationHealthWidgetProps {
  compact?: boolean;
}

const IntegrationHealthWidget = ({ compact = false }: IntegrationHealthWidgetProps) => {
  const { toast } = useToast();
  const { role, user } = useAuth();
  const canRunTests = hasHigherOrEqualRole(role, 'admin');

  const [health, setHealth] = useState<SystemHealthStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [runningHealthCheck, setRunningHealthCheck] = useState(false);

  const fetchHealth = async () => {
    try {
      const data = await getSystemHealth();
      setHealth(data);
    } catch (error) {
      console.error('Error fetching health:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealth();
  }, []);

  const runHealthCheck = async () => {
    setRunningHealthCheck(true);

    try {
      // Run all three tests in parallel
      const [googleRes, stripeRes, emailRes] = await Promise.all([
        supabase.functions.invoke('test-google-calendar'),
        supabase.functions.invoke('test-stripe'),
        supabase.functions.invoke('test-email'),
      ]);

      // Process results and update statuses
      const results = [
        { name: 'google_calendar' as const, data: googleRes.data, error: googleRes.error },
        { name: 'stripe' as const, data: stripeRes.data, error: stripeRes.error },
        { name: 'email' as const, data: emailRes.data, error: emailRes.error },
      ];

      for (const result of results) {
        if (result.error) {
          await updateIntegrationStatus(result.name, 'error', result.error.message);
          await logIntegrationTest(result.name, 'error', result.error.message, { error: result.error }, user?.id);
        } else if (result.data) {
          const status: IntegrationStatus = result.data.success
            ? 'success'
            : result.data.status === 'not_configured'
            ? 'not_configured'
            : 'error';
          await updateIntegrationStatus(result.name, status, result.data.message);
          await logIntegrationTest(
            result.name,
            result.data.success ? 'success' : 'error',
            result.data.message,
            { configured: result.data.configured },
            user?.id
          );
        }
      }

      // Refresh health data
      const newHealth = await getSystemHealth();
      setHealth(newHealth);

      // Log system health check
      const message = `Health check completed: ${newHealth.overall}`;
      await logSystemHealthCheck(newHealth.overall, message, {
        google_calendar: newHealth.integrations.google_calendar?.last_status,
        stripe: newHealth.integrations.stripe?.last_status,
        email: newHealth.integrations.email?.last_status,
      }, user?.id);

      toast({
        title: newHealth.overall === 'healthy' ? 'All Systems Operational' : 'Issues Detected',
        description: message,
        variant: newHealth.overall === 'healthy' ? 'default' : 'destructive',
      });
    } catch (error) {
      console.error('Health check error:', error);
      toast({
        title: 'Health Check Failed',
        description: 'An error occurred while running health checks.',
        variant: 'destructive',
      });
    } finally {
      setRunningHealthCheck(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="h-5 w-5" />
            System Health
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!health) {
    return null;
  }

  const StatusIcon = statusConfig[health.overall].icon;

  if (compact) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="h-5 w-5" />
              System Health
            </CardTitle>
            <Badge className={statusConfig[health.overall].className}>
              <StatusIcon className="h-3 w-3 mr-1" />
              {statusConfig[health.overall].label}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-3 gap-2 text-sm">
            <div className="flex items-center gap-1.5">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className={integrationStatusConfig[health.integrations.google_calendar?.last_status || 'not_checked'].className}>
                {integrationStatusConfig[health.integrations.google_calendar?.last_status || 'not_checked'].label}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <CreditCard className="h-4 w-4 text-muted-foreground" />
              <span className={integrationStatusConfig[health.integrations.stripe?.last_status || 'not_checked'].className}>
                {integrationStatusConfig[health.integrations.stripe?.last_status || 'not_checked'].label}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <span className={integrationStatusConfig[health.integrations.email?.last_status || 'not_checked'].className}>
                {integrationStatusConfig[health.integrations.email?.last_status || 'not_checked'].label}
              </span>
            </div>
          </div>

          {health.lastChecked && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Clock className="h-3 w-3" />
              Last checked {formatDistanceToNow(new Date(health.lastChecked), { addSuffix: true })}
            </div>
          )}

          <div className="flex gap-2">
            {canRunTests && (
              <Button
                size="sm"
                variant="outline"
                onClick={runHealthCheck}
                disabled={runningHealthCheck}
                className="flex-1"
              >
                {runningHealthCheck ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-1" />
                    Check Now
                  </>
                )}
              </Button>
            )}
            <Button size="sm" variant="ghost" asChild className="flex-1">
              <Link to="/admin/integrations">View Details</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="h-5 w-5" />
            System Health
          </CardTitle>
          <Badge className={statusConfig[health.overall].className}>
            <StatusIcon className="h-3 w-3 mr-1" />
            {statusConfig[health.overall].label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Integration Statuses */}
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="font-medium text-sm">Google Calendar</p>
                <p className="text-xs text-muted-foreground">
                  {health.integrations.google_calendar?.last_message || 'Not checked'}
                </p>
              </div>
            </div>
            <span className={`text-sm font-medium ${integrationStatusConfig[health.integrations.google_calendar?.last_status || 'not_checked'].className}`}>
              {integrationStatusConfig[health.integrations.google_calendar?.last_status || 'not_checked'].label}
            </span>
          </div>

          <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
            <div className="flex items-center gap-3">
              <CreditCard className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="font-medium text-sm">Stripe</p>
                <p className="text-xs text-muted-foreground">
                  {health.integrations.stripe?.last_message || 'Not checked'}
                </p>
              </div>
            </div>
            <span className={`text-sm font-medium ${integrationStatusConfig[health.integrations.stripe?.last_status || 'not_checked'].className}`}>
              {integrationStatusConfig[health.integrations.stripe?.last_status || 'not_checked'].label}
            </span>
          </div>

          <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="font-medium text-sm">Email Service</p>
                <p className="text-xs text-muted-foreground">
                  {health.integrations.email?.last_message || 'Not checked'}
                </p>
              </div>
            </div>
            <span className={`text-sm font-medium ${integrationStatusConfig[health.integrations.email?.last_status || 'not_checked'].className}`}>
              {integrationStatusConfig[health.integrations.email?.last_status || 'not_checked'].label}
            </span>
          </div>
        </div>

        {/* Last Checked */}
        {health.lastChecked && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            Last checked {formatDistanceToNow(new Date(health.lastChecked), { addSuffix: true })}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          {canRunTests && (
            <Button
              onClick={runHealthCheck}
              disabled={runningHealthCheck}
              className="flex-1"
            >
              {runningHealthCheck ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Run Health Check
                </>
              )}
            </Button>
          )}
          <Button variant="outline" asChild className={canRunTests ? '' : 'flex-1'}>
            <Link to="/admin/integrations">View All Integrations</Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default IntegrationHealthWidget;
